import { Link, useParams } from 'react-router-dom';
import {
    ArrowLeft,
    BookOpen,
    ExternalLink,
    GitBranch,
    LifeBuoy,
    Star,
} from 'lucide-react';
import { useApplication, useOpenApplication, useToggleFavorite } from '../hooks/useCatalog';
import { AppIcon } from '../components/AppIcon';
import {
    AccessBadge,
    AccessModeBadge,
    EnvironmentBadge,
    HealthDot,
    NetworkBadge,
    StatusBadge,
} from '../components/Badges';
import { ErrorState, LoadingState } from '../components/Feedback';
import {
    ACCESS_SOURCE_LABELS,
    appColor,
    cn,
    environmentLabel,
    formatDate,
    formatRelative,
} from '../lib/utils';
import type { Application } from '../types';

export function ApplicationDetailPage() {
    const { idOrSlug } = useParams<{ idOrSlug: string }>();
    const { data: application, isLoading, isError, error, refetch } = useApplication(idOrSlug);

    const toggleFavorite = useToggleFavorite();
    const openApplication = useOpenApplication();

    if (isLoading) return <LoadingState label="Chargement de l’application…" />;

    if (isError || !application) {
        return (
            <ErrorState
                message={
                    error instanceof Error ? error.message : 'Cette application est introuvable.'
                }
                onRetry={() => void refetch()}
            />
        );
    }

    return (
        <div className="space-y-6">
            <Link
                to="/"
                className="inline-flex items-center gap-1.5 text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-text)]"
            >
                <ArrowLeft className="w-4 h-4" aria-hidden />
                Retour au catalogue
            </Link>

            <header className="card card-padded">
                <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                    <div
                        className="app-swatch flex items-center justify-center w-14 h-14 rounded-xl shrink-0"
                        style={appColor(application.color)}
                        aria-hidden
                    >
                        <AppIcon name={application.icon} className="w-7 h-7" />
                    </div>

                    <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                            <h1 className="text-2xl font-bold text-[var(--color-text)]">
                                {application.name}
                            </h1>
                            <StatusBadge status={application.status} />
                        </div>

                        {application.category && (
                            <p className="mt-0.5 text-sm text-[var(--color-text-muted)]">
                                {application.category.name}
                                {application.ownerTeam && ` · ${application.ownerTeam}`}
                            </p>
                        )}

                        <p className="mt-3 text-sm text-[var(--color-text-secondary)] leading-relaxed">
                            {application.description ?? application.shortDescription}
                        </p>

                        <div className="mt-4 flex flex-wrap items-center gap-2">
                            <AccessBadge application={application} />
                            <AccessModeBadge mode={application.accessMode} />
                            {application.tags.map((tag) => (
                                <span key={tag} className="badge badge-neutral">
                                    {tag}
                                </span>
                            ))}
                        </div>
                    </div>

                    <div className="flex sm:flex-col gap-2 shrink-0">
                        {/* L'ouverture ne depend pas du droit enregistre : le Hub
                            catalogue, l'application verifie. Sans droit acquis le
                            bouton passe en retrait, pour ne pas laisser croire que
                            l'acces est accorde. */}
                        {application.instances.length > 0 && (
                            <button
                                type="button"
                                onClick={() => openApplication(application)}
                                className={application.hasAccess ? 'btn btn-primary' : 'btn btn-secondary'}
                                title={
                                    application.hasAccess
                                        ? undefined
                                        : 'Accès non enregistré dans le Hub : l’application vérifiera vos droits'
                                }
                            >
                                <ExternalLink className="w-4 h-4" aria-hidden />
                                Ouvrir
                            </button>
                        )}

                        <button
                            type="button"
                            onClick={() => toggleFavorite.mutate(application)}
                            className="btn btn-secondary"
                            aria-pressed={application.isFavorite}
                        >
                            <Star
                                className={cn(
                                    'w-4 h-4',
                                    application.isFavorite &&
                                        'fill-[var(--color-warning)] text-[var(--color-warning)]'
                                )}
                                aria-hidden
                            />
                            {application.isFavorite ? 'Favori' : 'Ajouter aux favoris'}
                        </button>
                    </div>
                </div>
            </header>

            <AccessNotice application={application} />

            <div className="grid gap-6 lg:grid-cols-3">
                <section className="lg:col-span-2 card">
                    <h2 className="px-5 py-3 text-sm font-semibold text-[var(--color-text-secondary)] border-b border-[var(--color-border)]">
                        Adresses et environnements
                    </h2>

                    {application.instances.length === 0 ? (
                        <p className="px-5 py-6 text-sm text-[var(--color-text-muted)]">
                            Cette application ne déclare aucune adresse web. Il peut s’agir d’une
                            application mobile ou d’un service accessible autrement.
                        </p>
                    ) : (
                        <ul className="divide-y divide-[var(--color-border)]">
                            {application.instances.map((instance) => (
                                <li key={instance.id} className="px-5 py-4">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className="font-medium text-sm text-[var(--color-text)]">
                                            {instance.label}
                                        </span>
                                        {/* La pastille d'environnement n'apparaît que si elle
                                            n'est pas déjà le libellé de l'instance : la plupart
                                            s'appellent « Production » ou « Développement », et la
                                            ligne affichait deux fois le même mot. TagMSP, dont
                                            l'instance « Test » est déclarée en recette, est
                                            justement le cas où la pastille apprend quelque
                                            chose. */}
                                        {environmentLabel(instance.environment) !==
                                            instance.label && (
                                            <EnvironmentBadge
                                                environment={instance.environment}
                                                short={false}
                                            />
                                        )}
                                        <NetworkBadge
                                            network={instance.network}
                                            requiresVpn={instance.requiresVpn}
                                        />
                                        {instance.healthStatus !== 'unmonitored' && (
                                            <span className="ml-auto">
                                                <HealthDot status={instance.healthStatus} />
                                            </span>
                                        )}
                                    </div>

                                    <div className="mt-1.5 flex items-center gap-2">
                                        {/* L'adresse etait affichee en texte mort quand
                                            le droit n'etait pas enregistre : elle se
                                            laissait copier mais pas suivre. Elle est
                                            cliquable dans les deux cas, en teinte plus
                                            sourde quand l'acces reste a obtenir. */}
                                        <button
                                            type="button"
                                            onClick={() =>
                                                openApplication(application, instance.id)
                                            }
                                            className={cn(
                                                'text-sm hover:underline break-all text-left',
                                                application.hasAccess
                                                    ? 'text-[var(--color-primary)]'
                                                    : 'text-[var(--color-text-secondary)]'
                                            )}
                                        >
                                            {instance.url}
                                        </button>
                                    </div>

                                    {instance.requiresVpn && (
                                        <p className="mt-1.5 text-xs text-[var(--color-network-private)]">
                                            Accessible uniquement depuis le réseau Clippit ou via le VPN.
                                        </p>
                                    )}

                                    {instance.healthCheckedAt && (
                                        <p className="mt-1 text-xs text-[var(--color-text-muted)]">
                                            Dernier contrôle {formatRelative(instance.healthCheckedAt)}
                                            {instance.healthLatencyMs !== null &&
                                                ` · ${instance.healthLatencyMs} ms`}
                                        </p>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                </section>

                <div className="space-y-6">
                    <section className="card card-padded space-y-3">
                        <h2 className="text-sm font-semibold text-[var(--color-text-secondary)]">
                            Ressources
                        </h2>

                        <ResourceLink
                            href={application.documentationUrl}
                            icon={<BookOpen className="w-4 h-4" aria-hidden />}
                            label="Documentation"
                        />
                        <ResourceLink
                            href={application.supportUrl}
                            icon={<LifeBuoy className="w-4 h-4" aria-hidden />}
                            label="Support"
                        />
                        <ResourceLink
                            href={application.repositoryUrl}
                            icon={<GitBranch className="w-4 h-4" aria-hidden />}
                            label="Code source"
                        />

                        {!application.documentationUrl &&
                            !application.supportUrl &&
                            !application.repositoryUrl && (
                                <p className="text-sm text-[var(--color-text-muted)]">
                                    Aucune ressource renseignée.
                                </p>
                            )}
                    </section>
                </div>
            </div>
        </div>
    );
}

/**
 * Bandeau qui explique l'état d'accès, en clair.
 *
 * L'accès est binaire (voir migration 022_droits_binaires.sql) : il n'y a
 * plus de demande à déposer depuis le Hub, seulement deux façons de
 * l'obtenir, qui ne dépendent pas de l'utilisateur lui-même — un
 * administrateur l'ajoute à la liste nominative, ou l'application est
 * rattachée à un groupe Entra ID dont il devient membre.
 */
function AccessNotice({ application }: { application: Application }) {
    if (application.hasAccess) {
        return (
            <div className="card p-4 border-l-4 border-l-[var(--color-success)]">
                <p className="text-sm text-[var(--color-text)]">
                    <strong>Vous avez accès à cette application.</strong>{' '}
                    {application.accessSource && ACCESS_SOURCE_LABELS[application.accessSource]}
                    {application.accessExpiresAt &&
                        ` · Jusqu’au ${formatDate(application.accessExpiresAt)}`}
                </p>
            </div>
        );
    }

    if (application.accessMode === 'entra_group') {
        return (
            <div className="card p-4 border-l-4 border-l-[var(--color-text-muted)]">
                <p className="text-sm text-[var(--color-text)]">
                    <strong>L’accès à cette application suit un groupe Entra ID dédié.</strong>{' '}
                    Rapprochez-vous de l’équipe qui en a la charge
                    {application.ownerTeam ? ` (${application.ownerTeam})` : ''} pour y être ajouté.
                </p>
            </div>
        );
    }

    return (
        <div className="card p-4 border-l-4 border-l-[var(--color-text-muted)]">
            <p className="text-sm text-[var(--color-text)]">
                <strong>Vous n’avez pas accès à cette application.</strong> Un administrateur du Hub
                peut vous y ajouter, ou rapprochez-vous de l’équipe qui en a la charge
                {application.ownerTeam ? ` (${application.ownerTeam})` : ''}.
            </p>
        </div>
    );
}

function ResourceLink({
    href,
    icon,
    label,
}: {
    href: string | null;
    icon: React.ReactNode;
    label: string;
}) {
    if (!href) return null;

    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-sm text-[var(--color-primary)] hover:underline"
        >
            {icon}
            {label}
            <ExternalLink className="w-3 h-3 opacity-60" aria-hidden />
        </a>
    );
}
